import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Download, FileText, ShieldCheck, Leaf, TrendingUp, Beaker } from "lucide-react";

const BROCHURE_URL = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/resources/company-brochure.pdf`;

const COPY = {
  en: {
    eyebrow: "Company Brochure · 2026",
    headline: "Nature's Purest Blue. Prepared for Global Launch.",
    sub: "India-Grown · Science-Graded · Preparing for July 2027. An initiative of Carbofixers.",
    download_label: "Download Company Brochure (PDF)",
    download_note: "Master overview PDF · updated for pre-launch 2026-27",
    section_overview_eyebrow: "01 · Company",
    section_overview_h: "Who we are",
    section_overview_body: "SPIRUVA is a premium phycocyanin & spirulina platform for buyers planning natural-blue supply, formulation development, and regulated-market launches ahead of July 2027 commercial readiness. Cultivated in India · Prepared for global supply. 300+ productive cultivation days per year.",
    pillars_eyebrow: "02 · Why SPIRUVA",
    pillars_h: "Four structural advantages",
    grade_eyebrow: "03 · Product Line",
    grade_h: "Grade architecture · E18 → E40",
    grade_body: "A single documented ladder — from formulation-grade E18 through advanced-technical E40 — so buyers can align product selection to their end-use compliance envelope without guesswork.",
    apps_eyebrow: "04 · Applications",
    apps_h: "16 industry verticals",
    apps_body: "Every SPIRUVA grade maps to a specific application-tier reality — from mainstream food-and-beverage colour systems through advanced diagnostic-grade fluorescence workflows.",
    positioning_eyebrow: "05 · Positioning",
    positioning_h: "Built for high-trust supply planning, not commodity trading.",
    positioning_body: "Natural-blue demand is accelerating as brands move off synthetic colorants. The advantage belongs to buyers who secure technical alignment, documentation pathways, and launch-stage allocation before demand peaks in the 2027-2029 window.",
    preorder_badge: "Early-Bird · Limited Pre-Launch Window",
    preorder_h: "Pre-Ordering Is Now Open.",
    preorder_body: "Reserve your 2027 allocation now — technical reviews, distributor discussions, and early allocation planning are open. Capacity is limited and prioritised by grade. Pre-booking partners receive early-bird pre-launch pricing and priority allocation advantage.",
    cta_reserve: "Reserve 2027 Allocation",
    cta_distributor: "Apply to Distributor Portal",
    contact_h: "Partner Intake Desk",
    contact_email: "export@spiruva.com",
    contact_note: "Commercial production and routine fulfilment targeted from July 2027. Technical review and partner intake now open.",
  },
  de: {
    eyebrow: "Unternehmens­broschüre · 2026",
    headline: "Das reinste Blau der Natur. Bereit für den globalen Launch.",
    sub: "Angebaut in Indien · Wissenschaftlich klassifiziert · Vorbereitet für Juli 2027. Eine Initiative von Carbofixers.",
    download_label: "Unternehmensbroschüre herunterladen (PDF)",
    download_note: "Master-Überblick PDF · aktualisiert für Pre-Launch 2026-27",
    section_overview_eyebrow: "01 · Unternehmen",
    section_overview_h: "Wer wir sind",
    section_overview_body: "SPIRUVA ist eine Premium-Phycocyanin- und Spirulina-Plattform für Einkäufer, die natürliche Blauversorgung, Formulierungsentwicklung und Markteinführungen in regulierten Märkten vor der kommerziellen Bereitschaft im Juli 2027 planen. In Indien kultiviert · Für globale Belieferung vorbereitet. 300+ produktive Kultivierungstage pro Jahr.",
    pillars_eyebrow: "02 · Warum SPIRUVA",
    pillars_h: "Vier strukturelle Vorteile",
    grade_eyebrow: "03 · Produktlinie",
    grade_h: "Grade-Architektur · E18 → E40",
    grade_body: "Eine dokumentierte Leiter — vom Formulierungs-Grade E18 bis zum Advanced-Technical E40 — damit Einkäufer die Produktauswahl ohne Rätselraten auf ihre Compliance-Envelope abstimmen können.",
    apps_eyebrow: "04 · Anwendungen",
    apps_h: "16 Industriezweige",
    apps_body: "Jede SPIRUVA-Grade entspricht einer spezifischen Anwendungsebene — von Mainstream-Farbsystemen für Lebensmittel und Getränke bis zu fortschrittlichen Diagnose-Fluoreszenz-Workflows.",
    positioning_eyebrow: "05 · Positionierung",
    positioning_h: "Konzipiert für vertrauensvolle Versorgungsplanung, nicht für Rohstoffhandel.",
    positioning_body: "Die Nachfrage nach natürlichem Blau beschleunigt sich, während Marken von synthetischen Farbstoffen abrücken. Der Vorteil liegt bei Einkäufern, die technische Abstimmung, Dokumentationspfade und Launch-Stage-Kontingente sichern, bevor die Nachfrage im Zeitfenster 2027–2029 ihren Höhepunkt erreicht.",
    preorder_badge: "Early-Bird · Begrenztes Pre-Launch-Fenster",
    preorder_h: "Vorbestellungen jetzt offen.",
    preorder_body: "Sichern Sie sich jetzt Ihr Kontingent für 2027 — technische Reviews, Distributor-Gespräche und frühzeitige Kontingentplanung sind eröffnet. Kapazität ist begrenzt und wird nach Grade priorisiert. Pre-Booking-Partner erhalten Early-Bird-Pre-Launch-Preise und Prioritäts-Vorteile bei der Kontingentzuteilung.",
    cta_reserve: "Kontingent 2027 reservieren",
    cta_distributor: "Zum Distributor-Portal",
    contact_h: "Partner-Intake-Desk",
    contact_email: "export@spiruva.com",
    contact_note: "Kommerzielle Produktion und regulärer Versand ab Juli 2027 geplant. Technischer Review und Partner-Aufnahme sind jetzt geöffnet.",
  },
  ja: {
    eyebrow: "会社案内 · 2026",
    headline: "自然界で最も純粋な青。世界展開の準備完了。",
    sub: "インド栽培 · サイエンスグレード · 2027年7月に向けて準備中。Carbofixers のイニシアティブ。",
    download_label: "会社案内をダウンロード (PDF)",
    download_note: "マスター概要 PDF · プレローンチ 2026-27 版",
    section_overview_eyebrow: "01 · 会社",
    section_overview_h: "SPIRUVA について",
    section_overview_body: "SPIRUVA は、2027 年 7 月の商業生産開始に先駆けて、天然ブルー原料の供給計画、処方開発、規制市場での立ち上げを検討しているバイヤー向けのプレミアム・フィコシアニン & スピルリナ・プラットフォームです。インドで栽培し、世界供給に向けて準備中。年間 300 日以上の生産可能日数。",
    pillars_eyebrow: "02 · SPIRUVA を選ぶ理由",
    pillars_h: "4 つの構造的優位性",
    grade_eyebrow: "03 · 製品ライン",
    grade_h: "グレード体系 · E18 → E40",
    grade_body: "処方用グレード E18 から高度技術グレード E40 まで、単一の文書化されたラダー。バイヤーは推測ではなく用途コンプライアンス要件に合わせて製品を選択できます。",
    apps_eyebrow: "04 · 用途",
    apps_h: "16 業界カテゴリー",
    apps_body: "各 SPIRUVA グレードは、食品・飲料の主要カラーシステムから、高度な診断グレードの蛍光ワークフローまで、特定の用途層に対応しています。",
    positioning_eyebrow: "05 · ポジショニング",
    positioning_h: "コモディティ取引ではなく、信頼性の高いサプライ計画のために設計。",
    positioning_body: "ブランドが合成着色料から離れる中、天然ブルーの需要は加速しています。2027 – 2029 年に需要がピークを迎える前に、技術的な連携、ドキュメント経路、ローンチ段階の供給枠を確保するバイヤーが優位に立ちます。",
    preorder_badge: "アーリーバード · 期間限定プレローンチ枠",
    preorder_h: "予約受付を開始しました。",
    preorder_body: "2027 年のアロケーション枠を今すぐ確保してください — 技術レビュー、ディストリビューター協議、早期アロケーション計画が開始されています。生産枠には限りがあり、グレードごとに優先順位が付けられます。予約パートナーには、アーリーバードのプレローンチ価格と優先アロケーション枠が提供されます。",
    cta_reserve: "2027 アロケーションを予約",
    cta_distributor: "ディストリビューター申請",
    contact_h: "パートナー窓口",
    contact_email: "export@spiruva.com",
    contact_note: "商業生産および定期出荷は 2027 年 7 月開始予定。技術レビューとパートナー受付は現在オープン。",
  },
};

const PILLARS = {
  en: [
    { icon: Leaf,        title: "India's Climate Advantage",       body: "300+ productive cultivation days per year — a structural yield edge most global regions cannot match." },
    { icon: Beaker,      title: "Purity You Can Measure",           body: "Every grade defined by an A620/A280 purity ratio, not marketing language. E18 · E25 · E30 · E40 form a documented ladder." },
    { icon: FileText,    title: "Documentation by Design",          body: "TDS · CoA framework · MoA-style pathway · export-ready certifications baked into the launch architecture — not bolted on after." },
    { icon: TrendingUp,  title: "Launch-Ready Market Positioning",  body: "India's FTA network (EU · UAE · Japan · South Korea · Australia) plus our compliance stack removes friction from premium-blue procurement." },
  ],
  de: [
    { icon: Leaf,        title: "Klimavorteil Indien",              body: "300+ produktive Kultivierungstage pro Jahr — ein struktureller Ertragsvorteil, den die meisten globalen Regionen nicht erreichen." },
    { icon: Beaker,      title: "Messbare Reinheit",                body: "Jede Grade wird durch ein A620/A280-Reinheitsverhältnis definiert, nicht durch Marketingsprache. E18 · E25 · E30 · E40 bilden eine dokumentierte Leiter." },
    { icon: FileText,    title: "Dokumentation von Anfang an",      body: "TDS · CoA-Framework · MoA-Pfad · exportfertige Zertifizierungen — in der Launch-Architektur verankert, nicht nachträglich hinzugefügt." },
    { icon: TrendingUp,  title: "Launch-fähige Marktpositionierung", body: "Indiens FTA-Netzwerk (EU · UAE · Japan · Südkorea · Australien) plus unser Compliance-Stack reduziert Reibung im Premium-Blau-Einkauf." },
  ],
  ja: [
    { icon: Leaf,        title: "インドの気候的優位性",              body: "年間 300 日以上の生産可能日数 — 世界の大半の地域では実現不可能な構造的な収量優位性。" },
    { icon: Beaker,      title: "測定可能な純度",                    body: "各グレードは A620/A280 純度比によって定義され、マーケティング言語ではありません。E18 · E25 · E30 · E40 が文書化されたラダーを形成。" },
    { icon: FileText,    title: "設計段階からのドキュメント",        body: "TDS · CoA フレームワーク · MoA スタイルパス · 輸出対応の認証を、後付けではなくローンチアーキテクチャの一部として組み込み。" },
    { icon: TrendingUp,  title: "ローンチ準備完了のポジショニング",   body: "インドの FTA ネットワーク (EU · UAE · 日本 · 韓国 · 豪州) と当社のコンプライアンススタックが、プレミアムブルーの調達摩擦を解消。" },
  ],
};

const GRADES = [
  { id: "E18", ratio: "≥ 0.7",  tier: "Food",                     forms: "Powder / Liquid Concentrate", apps: "Food coloring · beverages · confectionery · ice cream · dairy",           certs: "FSSAI · HACCP · Halal" },
  { id: "E25", ratio: "≥ 1.5",  tier: "Cosmetic / Nutraceutical",  forms: "Freeze-dried powder / Liquid", apps: "Skincare serums · supplements · functional beverages",                   certs: "ISO 22000 · Organic · HACCP" },
  { id: "E30", ratio: "≥ 3.0",  tier: "Pharma / Research",         forms: "Freeze-dried powder",          apps: "Pharma R&D · clinical nutrition · advanced bioactives · research",       certs: "WHO-GMP (in progress) · GLP" },
  { id: "E40", ratio: "≥ 4.0",  tier: "Advanced Technical",        forms: "High-purity · custom format",  apps: "Diagnostics · fluorescence-linked systems · institutional R&D",          certs: "Technical dossier pathway" },
];

const INDUSTRIES = [
  { group_en: "Human Nutrition & Personal Care",   items: ["Food & Beverage", "Cosmetics", "Nutraceuticals", "Pharma & Diagnostics", "Sports/Functional", "Clinical/Medical"] },
  { group_en: "Animal Nutrition & Veterinary",     items: ["Aquaculture", "Cattle & Dairy", "Poultry", "Pet Nutrition", "Equine", "Apiculture"] },
  { group_en: "Industrial & Frontier",             items: ["Biofertilizer & Agriculture", "Bioplastics & Textiles", "Space & Defense Nutrition"] },
];

const Brochure = () => {
  const { i18n } = useTranslation();
  const lc = (i18n.language || "en").split("-")[0];
  const t = COPY[lc] || COPY.en;
  const pillars = PILLARS[lc] || PILLARS.en;
  const prefix = lc === "en" ? "" : `/${lc}`;

  return (
    <div className="bg-brand-cream min-h-screen" data-testid="brochure-page">
      {/* Hero */}
      <section className="relative bg-brand-black text-brand-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #c9a84c 0, transparent 45%), radial-gradient(circle at 80% 70%, #006d77 0, transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-brand-gold mb-6" data-testid="brochure-eyebrow">{t.eyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" data-testid="brochure-headline">
            {t.headline}
          </h1>
          <p className="mt-6 text-base md:text-lg text-brand-cream/70 max-w-2xl">{t.sub}</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={BROCHURE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-download-cta"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-black px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-gold/90 transition"
            >
              <Download size={14} /> {t.download_label}
            </a>
            <Link
              to={`${prefix}/reserve-allocation`}
              data-testid="brochure-hero-reserve-cta"
              className="inline-flex items-center gap-2 border border-brand-cream/30 text-brand-cream px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-cream/10 transition"
            >
              {t.cta_reserve} <ArrowRight size={12} />
            </Link>
          </div>
          <p className="mt-5 text-[11px] font-mono tracking-[0.16em] text-brand-cream/45 uppercase">{t.download_note}</p>
        </div>
      </section>

      {/* 01 Company Overview */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">{t.section_overview_eyebrow}</p>
        <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-6" data-testid="brochure-overview-h">{t.section_overview_h}</h2>
        <p className="text-base md:text-lg text-brand-black/75 leading-relaxed max-w-3xl">{t.section_overview_body}</p>
      </section>

      {/* 02 Why SPIRUVA — 4 pillars */}
      <section className="bg-white border-y border-brand-green/15">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">{t.pillars_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-10">{t.pillars_h}</h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-7">
            {pillars.map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="p-6 md:p-7 bg-brand-cream/50 border border-brand-green/10" data-testid={`brochure-pillar-${i}`}>
                <Icon className="text-brand-teal mb-4" size={22} strokeWidth={1.5} />
                <h3 className="font-display text-xl text-brand-black leading-tight mb-2">{title}</h3>
                <p className="text-sm text-brand-black/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 Grade Architecture */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">{t.grade_eyebrow}</p>
        <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-4">{t.grade_h}</h2>
        <p className="text-base text-brand-black/70 max-w-3xl leading-relaxed mb-10">{t.grade_body}</p>

        {/* purity ladder */}
        <div className="grid grid-cols-4 gap-2 mb-10" data-testid="brochure-purity-ladder">
          {GRADES.map((g, i) => (
            <div key={g.id} className="text-center p-4 bg-white border border-brand-green/15">
              <div className="font-display text-2xl text-brand-black">{g.id}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand-teal mt-1">{g.ratio}</div>
              <div className="text-[11px] text-brand-black/55 mt-2">{g.tier}</div>
            </div>
          ))}
        </div>

        {/* grade rows */}
        <div className="space-y-4">
          {GRADES.map((g, i) => (
            <div key={g.id} className="grid md:grid-cols-[180px_1fr] gap-4 p-5 bg-white border border-brand-green/15" data-testid={`brochure-grade-${g.id.toLowerCase()}`}>
              <div>
                <div className="font-display text-2xl text-brand-black">Phycocyanin {g.id}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand-gold mt-1">{g.tier}</div>
              </div>
              <div className="text-sm text-brand-black/75 leading-relaxed space-y-1.5">
                <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">Purity ratio</span> · {g.ratio}</div>
                <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">Forms</span> · {g.forms}</div>
                <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">Applications</span> · {g.apps}</div>
                <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">Documentation</span> · {g.certs}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 04 Applications */}
      <section className="bg-white border-y border-brand-green/15">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">{t.apps_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-4">{t.apps_h}</h2>
          <p className="text-base text-brand-black/70 max-w-3xl leading-relaxed mb-10">{t.apps_body}</p>
          <div className="space-y-8">
            {INDUSTRIES.map((g, gi) => (
              <div key={gi} data-testid={`brochure-industry-group-${gi}`}>
                <h3 className="font-display text-lg text-brand-teal mb-4">{g.group_en}</h3>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((item, i) => (
                    <span key={i} className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.14em] bg-brand-cream border border-brand-green/15 text-brand-black/70">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 05 Positioning */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">{t.positioning_eyebrow}</p>
        <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-6 max-w-3xl leading-tight">{t.positioning_h}</h2>
        <p className="text-base md:text-lg text-brand-black/75 leading-relaxed max-w-3xl">{t.positioning_body}</p>
      </section>

      {/* 06 Pre-Order Callout */}
      <section className="bg-brand-black text-brand-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/50 text-brand-gold text-[10px] font-mono uppercase tracking-[0.22em] mb-6" data-testid="brochure-preorder-badge">
            <ShieldCheck size={12} /> {t.preorder_badge}
          </div>
          <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl" data-testid="brochure-preorder-h">{t.preorder_h}</h2>
          <p className="mt-6 text-base md:text-lg text-brand-cream/70 max-w-2xl leading-relaxed">{t.preorder_body}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to={`${prefix}/reserve-allocation`}
              data-testid="brochure-preorder-reserve-cta"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-black px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-gold/90 transition"
            >
              {t.cta_reserve} <ArrowRight size={12} />
            </Link>
            <Link
              to={`${prefix}/distributor-portal`}
              data-testid="brochure-distributor-cta"
              className="inline-flex items-center gap-2 border border-brand-cream/30 text-brand-cream px-6 py-3 text-xs font-mono uppercase tracking-[0.22em] hover:bg-brand-cream/10 transition"
            >
              {t.cta_distributor} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing / Contact */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-brand-green/15">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-gold mb-4">06 · Contact</p>
        <h2 className="font-display text-3xl md:text-4xl text-brand-black mb-6">{t.contact_h}</h2>
        <div className="space-y-2 text-base text-brand-black/75">
          <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">Email</span> · <a href={`mailto:${t.contact_email}`} className="text-brand-teal underline">{t.contact_email}</a></div>
          <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">WhatsApp Business</span> · on application</div>
          <div><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-black/55">LinkedIn</span> · <a href="https://linkedin.com/company/spiruva" target="_blank" rel="noopener noreferrer" className="text-brand-teal underline">linkedin.com/company/spiruva</a></div>
        </div>
        <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.18em] text-brand-black/55 max-w-2xl leading-relaxed">{t.contact_note}</p>
      </section>
    </div>
  );
};

export default Brochure;
